import React, { useState, useEffect } from "react";
import MainCalculatorsList from "./MainCalculators";
import RelatedLinks from "../RelatedLinks";
import { FirstAds, SecondAds, MobileUnderCalAds } from "../../Ads";
import SharedHelmet from "../Helmet";

const TITLE = "Calculatrice de la constante de solubilité (Ksp) - Calculez la solubilité et la concentration molaire";
const DESCRIPTION =
  "Calculatrice de la constante de solubilité (Ksp) - Calculez la solubilité et la concentration molaire. Calculateur de chimie en francais pour cours et TP, avec saisie simple, resultat rapide et interpretation claire.";
const KEYWORDS =
  "ksp calculator, calculatrice chimie, formule de chimie, tp de chimie, calcul en ligne";
const OG_URL = "https://do-calculate.com/calculator/fr/ksp-calculator/";

const SCHEMA = [
  {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "Calculatrice de constante de solubilité (Ksp)",
    description: DESCRIPTION,
    url: OG_URL,
    applicationCategory: "EducationApplication",
    operatingSystem: "Any",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
    },
  },
];

const SALT_TYPES = {
  AB: { label: "Sel binaire (AB) comme AgCl", x: 1, y: 1 },
  AB2: { label: "Sel ternaire (AB2) comme Mg(OH)2", x: 1, y: 2 },
  A2B: { label: "Sel ternaire (A2B) comme Ag2CrO4", x: 2, y: 1 },
  AB3: { label: "Sel quaternaire (AB3) comme Al(OH)3", x: 1, y: 3 },
};

function KspCalculator() {
  const [calcMode, setCalcMode] = useState("s_to_ksp");
  const [saltType, setSaltType] = useState("AB");
  const [molarSolubility, setMolarSolubility] = useState("");
  const [kspValue, setKspValue] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const calculateKsp = () => {
    try {
      setError("");
      const type = SALT_TYPES[saltType];

      if (calcMode === "s_to_ksp") {
        if (!molarSolubility) {
          setResult(null);
          return;
        }
        const s = parseFloat(molarSolubility);
        if (isNaN(s) || s <= 0) {
          setError("Veuillez entrer une solubilité valide et positive");
          setResult(null);
          return;
        }

        let ksp = 0;
        if (saltType === "AB") ksp = s * s;
        else if (saltType === "AB2") ksp = 4 * s * s * s;
        else if (saltType === "A2B") ksp = 4 * s * s * s;
        else if (saltType === "AB3") ksp = 27 * s * s * s * s;

        setResult({ ksp: ksp.toExponential(2), s });
      }
    } catch (err) {
      setError("Une erreur s'est produite lors du calcul.");
      setResult(null);
    }
  };

  useEffect(() => {
    if (calcMode === "s_to_ksp") calculateKsp();
  }, [molarSolubility, saltType, calcMode]);

  const handleClear = () => {
    setMolarSolubility("");
    setKspValue("");
    setResult(null);
    setError("");
  };

  return (
    <div className="ltr">
      <SharedHelmet
        TITLE={TITLE}
        DESCRIPTION={DESCRIPTION}
        KEYWORDS={KEYWORDS}
        OG_URL={OG_URL}
        SCHEMA={SCHEMA}
      />
      <div className="row">
        <div className="col-12 col-md-8">
          <FirstAds />
        </div>
      </div>
      <div className="row">
        <div className="col-12 col-md-8">
          <br />
          <div className="bg-success text-white text-center" style={{ padding: "15px" }}>
            <h1 style={{ fontSize: "22px", margin: 0 }}>{TITLE}</h1>
          </div>
          <div className="border border-light mt-3 p-3">
            <div className="mb-4">
              <p style={{ fontSize: "16px", lineHeight: "1.8" }}>
                La <strong>constante de produit de solubilité (Ksp)</strong> sont utilisées pour déterminer la solubilité des composés ioniques faiblement solubles dans l'eau.
              </p>
            </div>
            <hr />
            <MobileUnderCalAds />

            <div className="calculatorStyle mb-4">
              <div className="p-3 mb-3 bg-success text-white text-center rounded">
                <h2 style={{ fontSize: "18px", margin: 0 }}>Calcul de Ksp</h2>
              </div>

              <div className="form-group">
                <label>Mode de calcul</label>
                <select
                  className="form-control"
                  value={calcMode}
                  onChange={(e) => setCalcMode(e.target.value)}
                >
                  <option value="s_to_ksp">Calculer Ksp à partir de la solubilité</option>
                  <option value="ksp_to_s">Calculer la solubilité à partir de Ksp</option>
                </select>
              </div>

              <div className="form-group mt-3">
                <label>Type de sel</label>
                <select
                  className="form-control"
                  value={saltType}
                  onChange={(e) => setSaltType(e.target.value)}
                >
                  {Object.entries(SALT_TYPES).map(([key, { label }]) => (
                    <option key={key} value={key}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>

              {calcMode === "s_to_ksp" && (
                <div>
                  <div className="form-group mt-3">
                    <label>Solubilité molaire (mol/L)</label>
                    <input
                      type="number"
                      className="form-control"
                      value={molarSolubility}
                      onChange={(e) => setMolarSolubility(e.target.value)}
                      placeholder="Par exemple : 0.01"
                    />
                  </div>

                  <div className="row mt-3">
                    <div className="col-12">
                      <button className="btn btn-success" onClick={handleClear}>
                        Effacer
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {error && <div className="alert alert-danger">{error}</div>}

            {result && (
              <div className="p-3 mb-3 rounded bg-info">
                <h3 className="text-white">Résultats :</h3>
                <p className="text-white"><strong>Ksp = {result.ksp}</strong></p>
              </div>
            )}
          </div>
        </div>
        <div className="col">
          <SecondAds />
          <br />
          <MainCalculatorsList></MainCalculatorsList>
          <br />
        </div>
      </div>
      <br />
    </div>
  );
}
export default KspCalculator;
